import type { LessonPlanKind, LessonPlanStatus } from '@dataconnect/generated';

import { useMemo } from 'react';
import {
  periodLength,
  assemblePacing,
  type BellPeriod,
  type PacingResult,
  type PacingLesson,
  modalPeriodMinutes,
  placementsFromRows,
  type LessonPlacement,
} from '@classroom-ai/pacing';
import {
  useListLessonPlansByCourse,
  useListSchoolYearsBySchool,
  useListSpecialDaysBySchoolYear,
  useListCurriculumUnitsByCourse,
  useListDailySchedulesBySchoolYear,
  useListAcademicPeriodsBySchoolYear,
  useListClassLessonPlacementsByClass,
  useListScheduleAssignmentsBySchoolYear,
} from '@dataconnect/generated/react';

import { type TeacherClass, defaultSchedulePeriod } from 'src/class/class-provider';

// ----------------------------------------------------------------------
// Shared pacing view-model. Loads a schedule + the school-year calendar + the
// course's units/lessons, then runs `computePacing`. Consumed by the Pacing tab
// (full render), the Plan-unit wizard (per-unit content-day budget), and the
// weekly view. Keeping it in one hook avoids duplicating the seven calendar reads.
//
// The core `usePacingContext` is decoupled from `TeacherClass`: it takes a
// resolved `PacingContext` so it can compute pacing for (a) a class the teacher
// owns, (b) an arbitrary course section a Department Head selects, or (c) a
// class-less *baseline* on the school year's default daily schedule. `usePacing`
// is the back-compat adapter that derives a context from the active class.
//
// The calendar → pacing pipeline itself is `assemblePacing` in
// @classroom-ai/pacing — the agent runs the same function server-side.
// ----------------------------------------------------------------------

export { parseMinutes } from '@classroom-ai/pacing';

/** The bell-schedule period a section meets in, as consumed by pacing. */
export type PacingPeriod = BellPeriod;

/**
 * A resolved pacing scope, decoupled from `TeacherClass`. `meets` says how to
 * derive the meeting calendar + period length:
 *  - `{ scheduleIds, period }` — an actual section: the daily schedules it meets
 *    on and its period on the default schedule.
 *  - `'baseline'` — no specific class: use the school year's default daily
 *    schedule (every instructional day on it), sized to that schedule's modal
 *    period length. `classId` is null, so there is no per-class overlay.
 */
export type PacingContext = {
  courseId: string;
  schoolYearId: string;
  schoolId: string;
  /** The section whose per-class overlay applies, or null for a baseline. */
  classId: string | null;
  meets: { scheduleIds: Set<string>; period: PacingPeriod | null } | 'baseline';
};

export type PacingBackdrop = {
  academicPeriods: { id: string; name: string; startDate: string; endDate: string; sequence: number }[];
  specialDays: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    type: string;
    notes?: string | null;
    nonInstructional: boolean;
  }[];
  assignments: { date: string; scheduleName: string }[];
  schoolYear: { startDate: string; endDate: string } | null;
};

export type UsePacingResult = {
  isPending: boolean;
  /** The section's period, or a synthetic "Default schedule" period for a baseline; null if none. */
  period: PacingPeriod | null;
  periodMinutes: number | null;
  /** Meeting days a lesson can be placed on — the days pacing schedules into. */
  meetingDates: string[];
  /**
   * Meeting days on a schedule with "Lessons can be scheduled" off (epic #277, e.g.
   * Half Days): the class meets, but no lesson is placed. Shown greyed on the calendar.
   */
  noLessonDays: { date: string; scheduleName: string }[];
  /** Lessons this class actually runs (non-ARCHIVED, net of exclusions). */
  lessonCount: number;
  pacing: PacingResult | null;
  /** Titles for display (unitId → title, lessonId → title). */
  unitTitle: Map<string, string>;
  lessonTitle: Map<string, string>;
  /** lessonId → kind, so the calendar can colour Pre/Post assessments distinctly (epic #160). */
  lessonKind: Map<string, LessonPlanKind>;
  /** lessonId → status, so the weekly grid can flag Draft / not-yet-planned lessons (#418). */
  lessonStatus: Map<string, LessonPlanStatus>;
  /**
   * Every non-ARCHIVED lesson keyed by unitId (unsorted; INCLUDES class-excluded
   * lessons so the Pacing tab can offer to re-include them). Epic #123.
   */
  lessonsByUnit: Map<string, PacingLesson[]>;
  /** The active class's sparse pacing overlay, keyed by lessonId. Epic #123. */
  placements: Map<string, LessonPlacement>;
  backdrop: PacingBackdrop;
};

const EMPTY_BACKDROP: PacingBackdrop = {
  academicPeriods: [],
  specialDays: [],
  assignments: [],
  schoolYear: null,
};

const EMPTY_RESULT: UsePacingResult = {
  isPending: false,
  period: null,
  periodMinutes: null,
  meetingDates: [],
  noLessonDays: [],
  lessonCount: 0,
  pacing: null,
  unitTitle: new Map(),
  lessonTitle: new Map(),
  lessonKind: new Map(),
  lessonStatus: new Map(),
  lessonsByUnit: new Map(),
  placements: new Map(),
  backdrop: EMPTY_BACKDROP,
};

/**
 * Compute a pacing view-model for `ctx` at `targetPct` (0–1). Returns
 * `EMPTY_RESULT` when `ctx` is null. For a baseline (`meets: 'baseline'`) the
 * meeting calendar is the default daily schedule and the period is that
 * schedule's modal-length period; there is no per-class overlay.
 */
export function usePacingContext(ctx: PacingContext | null, targetPct: number): UsePacingResult {
  const courseId = ctx?.courseId ?? '';
  const schoolYearId = ctx?.schoolYearId ?? '';
  const schoolId = ctx?.schoolId ?? '';
  const classId = ctx?.classId ?? '';
  const isBaseline = ctx?.meets === 'baseline';

  const years = useListSchoolYearsBySchool({ schoolId }, { enabled: !!schoolId });
  const periods = useListAcademicPeriodsBySchoolYear({ schoolYearId }, { enabled: !!schoolYearId });
  const specialDaysQ = useListSpecialDaysBySchoolYear({ schoolYearId }, { enabled: !!schoolYearId });
  const schedulesQ = useListDailySchedulesBySchoolYear({ schoolYearId }, { enabled: !!schoolYearId });
  const assignmentsQ = useListScheduleAssignmentsBySchoolYear({ schoolYearId }, { enabled: !!schoolYearId });
  const unitsQ = useListCurriculumUnitsByCourse({ courseId }, { enabled: !!courseId });
  const lessonsQ = useListLessonPlansByCourse({ courseId }, { enabled: !!courseId });
  // Per-class overlay only exists for a real section, not a baseline.
  const placementsQ = useListClassLessonPlacementsByClass({ classId }, { enabled: !!classId });

  const schoolYear = years.data?.schoolYears.find((y) => y.id === schoolYearId) ?? null;
  const defaultSchedule = schedulesQ.data?.dailySchedules.find((d) => d.isDefault) ?? null;
  const defaultScheduleId = defaultSchedule?.id ?? null;

  // Baseline: the default schedule's modal period length (so a block-day outlier
  // period doesn't skew it). For a real section, the caller supplies the period.
  const baselineMinutes = useMemo<number | null>(() => {
    if (!isBaseline || !defaultSchedule) return null;
    return modalPeriodMinutes(defaultSchedule.periods ?? []);
  }, [isBaseline, defaultSchedule]);

  // Synthetic baseline display period; times are blank (the modal length has no
  // single start/end), so `periodMinutes` carries the real number.
  const period: PacingPeriod | null = isBaseline
    ? baselineMinutes != null
      ? { name: 'Default schedule', startTime: '', endTime: '' }
      : null
    : ctx && ctx.meets !== 'baseline'
      ? ctx.meets.period
      : null;
  const periodMinutes = isBaseline ? baselineMinutes : periodLength(period);

  // Which daily schedules the scope meets on. A baseline meets on the default
  // schedule; a real section carries its own set (from its schedule periods).
  const classScheduleIds = useMemo(() => {
    if (!ctx) return new Set<string>();
    if (ctx.meets === 'baseline') return new Set(defaultScheduleId ? [defaultScheduleId] : []);
    return ctx.meets.scheduleIds;
  }, [ctx, defaultScheduleId]);

  const isPending =
    !ctx ||
    years.isPending ||
    periods.isPending ||
    specialDaysQ.isPending ||
    schedulesQ.isPending ||
    assignmentsQ.isPending ||
    unitsQ.isPending ||
    lessonsQ.isPending ||
    (!!classId && placementsQ.isPending);

  // The active class's sparse pacing overlay, keyed by lessonId (epic #123).
  const placements = useMemo(
    () => placementsFromRows(placementsQ.data?.classLessonPlacements ?? []),
    [placementsQ.data]
  );

  const computed = useMemo(
    () =>
      assemblePacing({
        schoolYear: schoolYear ? { startDate: schoolYear.startDate, endDate: schoolYear.endDate } : null,
        specialDays: specialDaysQ.data?.specialDays ?? [],
        dailySchedules: schedulesQ.data?.dailySchedules ?? [],
        scheduleAssignments: assignmentsQ.data?.scheduleAssignments ?? [],
        units: unitsQ.data?.curriculumUnits ?? [],
        lessons: lessonsQ.data?.lessonPlans ?? [],
        academicPeriods: periods.data?.academicPeriods ?? [],
        placements,
        classScheduleIds,
        periodMinutes,
        targetPct,
      }),
    [
      schoolYear,
      specialDaysQ.data,
      assignmentsQ.data,
      schedulesQ.data,
      classScheduleIds,
      unitsQ.data,
      lessonsQ.data,
      periods.data,
      targetPct,
      periodMinutes,
      placements,
    ]
  );

  const unitTitle = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of unitsQ.data?.curriculumUnits ?? []) map.set(u.id, u.title);
    return map;
  }, [unitsQ.data]);

  const lessonTitle = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of lessonsQ.data?.lessonPlans ?? []) map.set(l.id, l.title);
    return map;
  }, [lessonsQ.data]);

  const lessonKind = useMemo(() => {
    const map = new Map<string, LessonPlanKind>();
    for (const l of lessonsQ.data?.lessonPlans ?? []) if (l.kind) map.set(l.id, l.kind);
    return map;
  }, [lessonsQ.data]);

  const lessonStatus = useMemo(() => {
    const map = new Map<string, LessonPlanStatus>();
    for (const l of lessonsQ.data?.lessonPlans ?? []) map.set(l.id, l.status);
    return map;
  }, [lessonsQ.data]);

  const backdrop = useMemo<PacingBackdrop>(
    () => ({
      academicPeriods: (periods.data?.academicPeriods ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        startDate: p.startDate,
        endDate: p.endDate,
        sequence: p.sequence,
      })),
      specialDays: (specialDaysQ.data?.specialDays ?? []).map((d) => ({
        id: d.id,
        name: d.name,
        startDate: d.startDate,
        endDate: d.endDate,
        type: d.type,
        notes: d.notes,
        nonInstructional: d.nonInstructional,
      })),
      assignments: (assignmentsQ.data?.scheduleAssignments ?? []).map((a) => ({
        date: a.date,
        scheduleName: a.dailySchedule.name,
      })),
      schoolYear: schoolYear ? { startDate: schoolYear.startDate, endDate: schoolYear.endDate } : null,
    }),
    [periods.data, specialDaysQ.data, assignmentsQ.data, schoolYear]
  );

  if (!ctx) return EMPTY_RESULT;

  return {
    isPending,
    period,
    periodMinutes,
    meetingDates: computed.meetingDates,
    noLessonDays: computed.noLessonDays,
    lessonCount: computed.lessonCount,
    pacing: computed.pacing,
    unitTitle,
    lessonTitle,
    lessonKind,
    lessonStatus,
    lessonsByUnit: computed.lessonsByUnit,
    placements,
    backdrop,
  };
}

/**
 * Back-compat adapter: compute the pacing view-model for the active class.
 * Returns an empty result when there's no active class (callers that only make
 * sense with a class — the weekly view, the unit wizard's budget). The Pacing
 * tab uses `usePacingContext` directly to also support baselines + arbitrary
 * sections a Department Head selects.
 */
export function usePacing(activeClass: TeacherClass | null, targetPct: number): UsePacingResult {
  const ctx = useMemo<PacingContext | null>(() => {
    if (!activeClass) return null;
    return {
      courseId: activeClass.course.id,
      schoolYearId: activeClass.course.schoolYear.id,
      schoolId: activeClass.course.schoolYear.school.id,
      classId: activeClass.id,
      meets: {
        scheduleIds: new Set((activeClass.schedulePeriods ?? []).map((sp) => sp.dailySchedule.id)),
        period: defaultSchedulePeriod(activeClass),
      },
    };
  }, [activeClass]);

  return usePacingContext(ctx, targetPct);
}
